<script setup lang="ts">
/**
 * The login dialog (07 §5, AP-F5): "e-mail first" after ADR-078 in the frame of the design
 * prototype (`#signinModal`), plus the staff entry without an instance (ADR-065).
 *
 * - The e-mail address goes to `POST /api/auth/login/start` first. The API decides: it starts
 *   the flow (`authorizeUrl`, the page leaves), or asks for the instance (`next: "instance"`,
 *   with `choices` when the address has several). When the lookup does not answer
 *   (`upstream`), the dialog offers the instance field as well (08 §1).
 * - The instance goes to the same route as the second step.
 * - A message with title and sentence for every code of `/login?error=` and for the errors of the
 *   start (`src/lib/login-error-text.ts`), with "Noch einmal versuchen" for the callback's; a
 *   note when the dialog opened because the session ended.
 * - For `instance_not_enabled` a button to apply for the beta (`apply` with the entry
 *   `instance`, whose intro says why, ADR-093 point 8); on the e-mail step "Noch kein Zugang? Für
 *   die Beta bewerben" (B-35, AP-F10.1), without an entry. Both asked for access before; in the
 *   alpha release the application takes its place (TL, 2026-10-27), and `App.vue` leads both to
 *   the application page `/beta` (ADR-143 addendum 3).
 *
 * Which step shows follows from the answer of the lookup in the mutation, so the server's
 * answer has one place (ADR-076 point 3). Lookup and start are two mutations, so a pending
 * start never hides the step it came from.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, useTemplateRef, watch } from "vue";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import AppDialog from "@ai-platform/ui/AppDialog.vue";
import LoginEmailForm from "./LoginEmailForm.vue";
import LoginInstanceForm from "./LoginInstanceForm.vue";
import { ApiRequestError } from "../api/client.ts";
import { useLoginStart, type Navigate } from "../composables/auth.ts";
import {
  loginErrorText,
  loginStartErrorText,
  LOOKUP_UNAVAILABLE_TEXT,
  type LoginMessage,
} from "../lib/login-error-text.ts";
import { safeReturnPath } from "../lib/return-path.ts";

const props = withDefaults(
  defineProps<{
    /** where to go after the login, from `?next=`; only relative paths count */
    next?: string | undefined;
    /** `?error=` of the callback (08 §1) */
    error?: string | undefined;
    /** the session ended while the user was working (`?ended=1`, set by the router) */
    sessionEnded?: boolean | undefined;
    /** leaves the page; tests replace it */
    navigate?: Navigate | undefined;
    /** false without the guest view: nothing stands behind the login, so it stays (ADR-133 point 3) */
    closable?: boolean | undefined;
  }>(),
  { closable: true },
);

/** `apply` names the entry of the application: `instance` after `instance_not_enabled`, none without an instance. */
const emit = defineEmits<{ close: []; apply: [lead?: "instance"] }>();

/**
 * Set once the dialog asked to close. Its owner closes it by a navigation that unmounts it a
 * moment later; until then the dialog is closed already, so `AppDialog` does not open it again
 * after the browser closed it on a second Escape (PF3).
 */
const closing = ref(false);

function close() {
  closing.value = true;
  emit("close");
}

/** the lookup of the e-mail address, step one */
const lookup = useLoginStart(props.navigate);
/** the start with an instance (step two) or as staff */
const start = useLoginStart(props.navigate);

/** the address of the last lookup, shown on the instance step; browser state, the user's input */
const email = ref("");
/** the user started a login in this dialog; from then on the messages of the arrival are done */
const restarted = ref(false);

const returnTo = computed(() => {
  const path = safeReturnPath(props.next);
  return path ? { returnTo: path } : {};
});

const codeOf = (error: unknown) => (error instanceof ApiRequestError ? error.code : undefined);
const leavesFor = (answer: unknown) => typeof answer === "object" && answer !== null && "authorizeUrl" in answer;

/** The instance step, from the lookup's answer; `null` shows the e-mail step. */
const instanceStep = computed<{ choices: readonly string[] } | null>(() => {
  const answer = lookup.data.value;
  // Asks for the step by name: a later step (`next: "password"`, ADR-071) gets its own form.
  if (answer && "next" in answer && answer.next === "instance") return { choices: answer.choices ?? [] };
  if (codeOf(lookup.error.value) === "upstream") return { choices: [] };
  return null;
});

/** Leaving for the authorize address: nothing may start twice meanwhile. */
const leaving = computed(() => leavesFor(lookup.data.value) || leavesFor(start.data.value));
const busy = computed(() => lookup.isPending.value || start.isPending.value || leaving.value);

/** One message at a time: a failed start, else the callback's error until the user starts again. */
const failure = computed<LoginMessage | null>(() => {
  if (start.error.value) return loginStartErrorText(codeOf(start.error.value));
  const lookupError = lookup.error.value;
  if (lookupError) {
    const code = codeOf(lookupError);
    return code === "upstream" ? LOOKUP_UNAVAILABLE_TEXT : loginStartErrorText(code);
  }
  if (restarted.value) return null;
  return loginErrorText(props.error);
});

/** The message is the callback's, from an earlier attempt: the user may start over from here. */
const arrival = computed(() => failure.value !== null && !start.error.value && !lookup.error.value);

const showSessionEnded = computed(() => props.sessionEnded === true && !props.error && !restarted.value);

/** The instance is not enabled yet: the way on is an inquiry, not another login. */
const notEnabled = computed(() => !restarted.value && props.error === "instance_not_enabled");

/**
 * The subtitle says why the dialog is there: asking for or ordering a product, or the login
 * itself (B-33). A guest cannot know whether the customer's own ordering is on, so the words hold
 * in both phases (ADR-133, TL 2026-10-20).
 */
const description = computed(() =>
  safeReturnPath(props.next)?.startsWith("/catalog")
    ? "Zum Anfragen und Bestellen meldet euch bitte an."
    : "Mit eurem dataCycle-Konto.",
);

// The messages are there when the dialog opens; naming them in its description has them read
// with it, which a live region alone does not do reliably.
const noteId = useId();
const failureId = useId();
const describedBy = computed(
  () => [showSessionEnded.value ? noteId : "", failure.value ? failureId : ""].filter(Boolean).join(" ") || undefined,
);

// While a lookup or a start runs, a form sent again starts nothing: the disabled button does not
// stop every way to submit a form, and a second start would open a second login next to the
// first. The staff entry is a plain button, disabled meanwhile.
function lookUp(address: string) {
  if (busy.value) return;
  restarted.value = true;
  start.reset();
  email.value = address;
  lookup.mutate({ email: address, ...returnTo.value });
}

function startInstance(instanceHost: string) {
  if (busy.value) return;
  restarted.value = true;
  start.mutate({ instanceHost, ...returnTo.value });
}

function startStaff() {
  restarted.value = true;
  start.mutate({ staff: true, ...returnTo.value });
}

function back() {
  lookup.reset();
  start.reset();
}

/** Leaves the callback's message behind and puts the user back into the e-mail step. */
function tryAgain() {
  restarted.value = true;
  back();
  void nextTick(focusStep);
}

// Focus goes to the step's first field when the dialog opens and whenever the step changes, so
// a keyboard or screen reader user lands in the form, not on the close button.
const stepElement = useTemplateRef<HTMLElement>("step");
const focusStep = () => stepElement.value?.querySelector<HTMLElement>("input, button")?.focus();
watch(
  () => instanceStep.value !== null,
  async () => {
    await nextTick();
    focusStep();
  },
);

// Back from the authorize address can restore this page from the browser's cache with its
// state: the answer that sent it away must not keep the buttons locked.
function onPageShow(event: PageTransitionEvent) {
  if (!event.persisted) return;
  if (leavesFor(lookup.data.value)) lookup.reset();
  if (leavesFor(start.data.value)) start.reset();
}

onMounted(() => {
  focusStep();
  window.addEventListener("pageshow", onPageShow);
});
onBeforeUnmount(() => window.removeEventListener("pageshow", onPageShow));
</script>

<template>
  <AppDialog
    :open="!closing"
    title="Anmelden"
    :description="description"
    :described-by="describedBy"
    :closable="props.closable"
    @close="close"
  >
    <p
      v-if="showSessionEnded"
      :id="noteId"
      role="status"
      class="rounded-control border-accent-line bg-accent-bg text-ui text-accent-text mt-0 mb-4 border px-3 py-2"
    >
      Eure Sitzung ist beendet. Bitte meldet euch neu an.
    </p>
    <div
      v-if="failure"
      :id="failureId"
      role="alert"
      class="rounded-control border-bad-line bg-bad-bg text-ui text-bad-text mt-0 mb-4 border px-3 py-2"
    >
      <p class="m-0 font-semibold">{{ failure.title }}</p>
      <p class="m-0 mt-1">{{ failure.text }}</p>
    </div>
    <PixButton
      v-if="notEnabled"
      class="text-ui mb-6 min-h-(--size-button) w-full font-bold"
      data-action="apply"
      @click="emit('apply', 'instance')"
    >
      Für die Beta bewerben
    </PixButton>
    <PixButton
      v-else-if="arrival"
      theme="secondary"
      outline
      class="text-ui mb-6 min-h-(--size-button) w-full font-semibold"
      data-action="try-again"
      @click="tryAgain"
    >
      Noch einmal versuchen
    </PixButton>

    <div ref="step">
      <LoginInstanceForm
        v-if="instanceStep"
        :choices="instanceStep.choices"
        :email="email"
        :pending="busy"
        :lookup-failed="codeOf(lookup.error.value) === 'upstream'"
        @submit="startInstance"
        @back="back"
      />
      <template v-else>
        <LoginEmailForm :pending="busy" :initial="email" @submit="lookUp" />
        <p v-if="!notEnabled" class="text-meta text-text-soft mt-4 mb-0" data-no-instance>
          Noch kein Zugang?
          <button
            type="button"
            class="rounded-chip text-accent-text focus-visible:outline-focus cursor-pointer border-0 bg-transparent p-0 font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            data-action="no-instance"
            @click="emit('apply')"
          >
            Für die Beta bewerben
          </button>
        </p>
      </template>
    </div>

    <div
      class="text-meta text-text-soft before:bg-line after:bg-line my-5 flex items-center gap-3 before:h-px before:flex-1 after:h-px after:flex-1"
    >
      Für Mitarbeiter
    </div>
    <PixButton outline class="text-ui min-h-(--size-button) w-full font-semibold" :disabled="busy" @click="startStaff">
      Als Mitarbeiter anmelden
    </PixButton>
  </AppDialog>
</template>
